import {
  isDevMode,
  Component,
  Output,
  EventEmitter,
  inject,
  OnInit,
  OnDestroy,
  computed,
  signal,
  InputSignal,
  input,
  ViewChildren,
  QueryList,
  ElementRef,
  AfterViewInit,
  ModelSignal,
  model,
} from "@angular/core";
import { getAdManager } from "../../../../capacitor-plugins/ad-manager";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { AdState } from "@bitwarden/web-vault/app/models/interfaces/credits.interfaces";
import { AdStateService } from "@bitwarden/web-vault/app/services/credits/ad-state.service";
import { GlobalService } from "@bitwarden/common/services/global/global.service";
import { CreditsService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/credits.service";
import { GlossCreditsTransactionSyncService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/gloss-credits-transaction-sync.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { APIStageType } from "@bitwarden/web-vault/app/models/types/environement-config.type";
import { GLOSS_PLATINUM_CREDITS_SYMBOL } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { WATCH_AD_REWARD } from "@bitwarden/web-vault/app/models/constants/credits.constants";

/** Which ad (if any) the in-app guide is currently pointing at — computed once per ad by
 *  getGuideHighlightFlags() and shared by getContainerClass/getContainerBg. */
interface GuideHighlightFlags {
  isFirstUnwatched: boolean;
  isFirstReady: boolean;
  isLastCompleted: boolean;
}

export enum AdStatus {
  Completed = "Completed",
  Interrupted = "Interrupted",
  Ready = "Ready",
  Locked = "Locked",
  NoAdAvailable = "NoAdAvailable",
  CoolDown = "CoolDown",
}

@Component({
  selector: "app-ad-list",
  templateUrl: "./ad-list.component.html",
})
export class AdListComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChildren("AdItemEl") itemElements!: QueryList<ElementRef>;
  protected adStateService = inject(AdStateService);
  private creditsService: CreditsService = inject(CreditsService);
  private globalService = inject(GlobalService);
  private glossCreditsTransactionSync: GlossCreditsTransactionSyncService = inject(
    GlossCreditsTransactionSyncService,
  );
  private platformUtils: PlatformUtilsService = inject(PlatformUtilsService);
  private adManager = getAdManager(inject(LogService));

  tryAgainLaterInTutorial: ModelSignal<boolean> = model(false);
  isWatchAdTaskSucceeded: ModelSignal<boolean> = model(false);
  isFromGuide: InputSignal<boolean> = input(false);
  isFromGuideViewPoints: InputSignal<boolean> = input(false);
  guideTitle: InputSignal<string> = input("");

  protected readonly watchAdReward = WATCH_AD_REWARD;

  @Output() viewBundleClick = new EventEmitter<void>();
  @Output() createBundleClick = new EventEmitter<void>();

  private adListeners: any[] = [];
  private currentAdIndex: number | null = null;
  private loadingTimeout: ReturnType<typeof setTimeout> | null = null;
  private isPrepared = false;
  private preloadAdIndex: number | null = null;
  private preloadTimeout: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  protected showDesktopModal = signal(false);
  protected readonly devMode = isDevMode();

  DataBundleSectionReady = false; // Set to true when the data bundle section is ready to be shown

  private readonly isDesktop: boolean = !this.platformUtils.isMobileDevice();

  protected highlightedAdIndex = computed(() => {
    if (!this.isFromGuide()) {
      return -1;
    }

    const models = this.adViewModels();
    if (this.isWatchAdTaskSucceeded()) {
      return models.reduce(
        (lastIdx, vm, i) => (vm.status === AdStatus.Completed ? i : lastIdx),
        -1,
      );
    }
    if (this.tryAgainLaterInTutorial()) {
      return this.getTryAgainTargetIndex();
    }
    return models.findIndex((vm) => vm.status === AdStatus.Ready);
  });

  private getTryAgainTargetIndex(): number {
    const idx = this.adViewModels().findIndex(
      (ad) => ad.status === AdStatus.Locked && ad.timeRemaining > 0,
    );
    return idx !== -1 ? idx : this.adStateService.adStates().findIndex((ad) => !ad.watched);
  }

  protected adViewModels = computed(() => {
    return this.adStateService.adStates().map((ad) => {
      const status = this.getAdStatus(ad);
      const iconConfig = this.getIconConfig(status);
      const highlightFlags = this.getGuideHighlightFlags(ad);

      return {
        id: ad.id,
        isLoading: ad.isLoading,
        status,
        showTimer: ad.locked && ad.timeRemaining > 0,
        timeRemaining: ad.timeRemaining,
        isDisabled:
          this.isFromGuideViewPoints() ||
          status === AdStatus.Completed ||
          status === AdStatus.Locked ||
          status === AdStatus.CoolDown ||
          ad.isLoading,

        // CSS Classes
        containerClass: this.getContainerClass(status, highlightFlags),
        containerBg: this.getContainerBg(status, highlightFlags),
        titleClass: this.getTitleClass(status),
        descriptionClass: "tw-text-color-text-secondary",
        pointsClass: this.getPointsClass(status),

        // Icon Config
        iconName: iconConfig.name,
        iconSize: iconConfig.size,
        iconClass: iconConfig.class,
        iconBgClass: iconConfig.bg,

        // Text Content
        title: this.getAdTitle(status),
        description: this.getAdDescription(status, ad.timeRemaining),
        points:
          status === AdStatus.Interrupted || status === AdStatus.NoAdAvailable
            ? "0pts"
            : `+${WATCH_AD_REWARD}CrP`,
      };
    });
  });

  async ngAfterViewInit() {
    if (this.isFromGuide() && this.adViewModels().length > 0) {
      let targetIndex = this.adViewModels().findIndex((ad) => ad.status === AdStatus.Ready);
      if (targetIndex === -1 && !this.isWatchAdTaskSucceeded()) {
        this.tryAgainLaterInTutorial.set(true);
        targetIndex = this.getTryAgainTargetIndex();
      }
      if (targetIndex !== -1) {
        const elementArray = this.itemElements.toArray();
        const targetElement = elementArray[targetIndex].nativeElement;
        await this.startWatchAdTutorial(targetElement);
      }
    }
  }

  async ngOnInit(): Promise<void> {
    this.adStateService.clearStuckLoadingStates();

    // Only fires for a show; preload failures arrive via prepareAd()'s rejection.
    const failedListener = await this.adManager.subscribeToFailedToLoad((error) => {
      if (this.currentAdIndex !== null) {
        this.adFailedToLoad(this.currentAdIndex);
        this.currentAdIndex = null;
      }
    });

    const loadedListener = await this.adManager.subscribeToLoaded((info) => {
      if (this.currentAdIndex !== null) {
        this.onLoaded(this.currentAdIndex);
      } else if (this.preloadAdIndex !== null) {
        clearTimeout(this.preloadTimeout);
        this.updateGlobalState(this.preloadAdIndex, { isLoading: false });
        this.preloadAdIndex = null;
        this.isPrepared = true;
      }
    });

    const rewardedListener = await this.adManager.subscribeToRewarded(async (reward) => {
      // Snapshot and clear currentAdIndex before any awaits below -- otherwise the
      // watchdog or a stray failedListener firing mid-await could null it out from
      // under a slow network call and silently drop an already-granted reward.
      // currentAdIndex can legitimately already be null here (a slow non-prepared load
      // tripped the watchdog before the ad finished loading, or an Android prepared-show
      // failure nulled it before the fallback showAd()'s reward arrived) -- the tx/sync
      // below must still run in that case since the server already granted the reward.
      const adIndex = this.currentAdIndex;
      this.currentAdIndex = null;
      if (adIndex !== null) {
        // onRewarded is client-side UI state only (marks the ad watched, bumps the daily
        // count, starts the next-ad cooldown); it does not grant credits, so it always
        // runs immediately rather than waiting on the network calls below.
        this.onRewarded(adIndex);
      }

      await this.grantReward();
    });

    const dismissedListener = await this.adManager.subscribeToDismissed(() => {
      // Clear the stuck loading state left behind when the user closes the ad early,
      // since showRewardVideoAd() never settles (no Loaded, no Rewarded, no rejection)
      // on early dismissal. Keep currentAdIndex: LevelPlay doesn't guarantee Rewarded
      // arrives before Dismissed, and a late Rewarded still has to credit this slot.
      // The next tap overwrites it.
      if (this.currentAdIndex !== null) {
        this.updateGlobalState(this.currentAdIndex, { isLoading: false });
        this.clearWatchdog();
      }
    });

    const failedToShowListener = await this.adManager.subscribeToFailedToShow(() => {
      // A loaded ad that fails to open (expired prepared ad, app backgrounded on tap,
      // mediation show error) only emits FailedToShow -- showRewardVideoAd() never
      // rejects and neither Dismissed nor FailedToLoad fires, so without this the slot
      // stays stuck in isLoading with no watchdog on the prepared path to recover it.
      this.releaseAbandonedShow();
    });

    this.adListeners.push(
      failedListener,
      loadedListener,
      rewardedListener,
      dismissedListener,
      failedToShowListener,
    );

    this.preloadNextReadyAd();
  }

  private preloadNextReadyAd(): void {
    if (this.isFromGuideViewPoints()) {
      return;
    }

    // Desktop watches ads through the AdSense modal, not native AdMob — preloading
    // a native rewarded ad there does nothing and would leave the first ad stuck in
    // a loading state on page load.
    if (this.isDesktop) {
      return;
    }

    const adIndex = this.adStateService
      .adStates()
      .findIndex((ad) => this.getAdStatus(ad) === AdStatus.Ready);
    if (adIndex === -1) {
      return;
    }

    this.preloadAdIndex = adIndex;
    this.isPrepared = false;
    this.adInteractedWith(adIndex);

    this.preloadTimeout = setTimeout(() => {
      if (this.destroyed || this.preloadAdIndex !== adIndex) {
        return;
      }
      this.updateGlobalState(adIndex, { isLoading: false });
      this.preloadAdIndex = null;
    }, 15000);

    this.adManager.prepareAd(this.creditsService.userId() ?? undefined).catch(() => {
      clearTimeout(this.preloadTimeout);
      if (this.destroyed || this.preloadAdIndex !== adIndex) {
        return;
      }
      this.updateGlobalState(adIndex, { isLoading: false });
      this.preloadAdIndex = null;
    });
  }

  async startWatchAdTutorial(element: HTMLElement): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, 100));
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  formatTimeRemaining(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  }

  private getAdStatus(ad: AdState): AdStatus {
    if (ad.watched) {
      return AdStatus.Completed;
    }
    if (ad.isLoaded && !ad.watched) {
      return AdStatus.Interrupted;
    }
    if (ad.locked && ad.timeRemaining > 0) {
      return AdStatus.CoolDown;
    }
    if (ad.locked) {
      return AdStatus.Locked;
    }
    if (ad.hasNoAds) {
      return AdStatus.NoAdAvailable;
    }
    return AdStatus.Ready;
  }

  // --- Style & Text Resolvers (Private Helpers) ---

  private getIconConfig(status: AdStatus) {
    const configs: Record<AdStatus, { name: string; size: number; class: string; bg: string }> = {
      [AdStatus.Ready]: {
        name: "right-arrow",
        size: 5,
        class: "tw-text-color-action-primary",
        bg: "tw-bg-color-accent-today-bg",
      },
      [AdStatus.CoolDown]: {
        name: "lock",
        size: 5,
        class: "tw-text-color-text-disabled",
        bg: "tw-bg-color-surface-sunken",
      },
      [AdStatus.Locked]: {
        name: "lock",
        size: 5,
        class: "tw-text-color-text-disabled",
        bg: "tw-bg-color-surface-sunken",
      },
      [AdStatus.Completed]: {
        name: "completed",
        size: 5,
        class: "tw-text-color-status-success-text",
        bg: "tw-bg-color-status-success-bg",
      },
      [AdStatus.Interrupted]: {
        name: "info-tooltip",
        size: 6,
        class: "tw-text-color-status-warning-text",
        bg: "tw-bg-color-surface-elevated",
      },
      [AdStatus.NoAdAvailable]: {
        name: "no-ads-available",
        size: 6,
        class: "tw-text-color-status-warning-text",
        bg: "tw-bg-color-surface-elevated",
      },
    };
    return configs[status];
  }

  /** Single pass over adStates() for the "is this ad the one the guide is pointing at right
   *  now" flags — shared by getContainerClass/getContainerBg so the highlight rule (and any
   *  future AdStatus case added to it) only has to be expressed once. */
  private getGuideHighlightFlags(ad: AdState): GuideHighlightFlags {
    const adStates = this.adStateService.adStates();
    const firstUnwatchedAd = adStates.find((a) => !a.watched);
    const isFirstUnwatched = firstUnwatchedAd?.id === ad.id;
    const firstReadyAd = adStates.find((a) => this.getAdStatus(a) === AdStatus.Ready);
    const isFirstReady = firstReadyAd?.id === ad.id;
    const watchedAds = adStates.filter((a) => a.watched);
    const isLastCompleted = watchedAds[watchedAds.length - 1]?.id === ad.id;
    return { isFirstUnwatched, isFirstReady, isLastCompleted };
  }

  private getContainerClass(
    status: AdStatus,
    { isFirstUnwatched, isFirstReady, isLastCompleted }: GuideHighlightFlags,
  ) {
    return {
      "tw-cursor-not-allowed":
        status === AdStatus.Locked ||
        status === AdStatus.CoolDown ||
        (status === AdStatus.Completed &&
          !(this.isFromGuide() && this.isWatchAdTaskSucceeded() && isLastCompleted)),
      "tw-border-2 tw-border-solid tw-border-color-action-primary": status === AdStatus.Ready,
      "tw-z-[100] tw-relative tw-overscroll-none":
        (status === AdStatus.Ready && this.isFromGuide() && isFirstReady) ||
        (status === AdStatus.Completed &&
          this.isFromGuide() &&
          this.isWatchAdTaskSucceeded() &&
          isLastCompleted) ||
        (status === AdStatus.CoolDown &&
          this.isFromGuide() &&
          this.tryAgainLaterInTutorial() &&
          isFirstUnwatched) ||
        (status === AdStatus.Locked &&
          this.isFromGuide() &&
          this.tryAgainLaterInTutorial() &&
          isFirstUnwatched),
      "tw-z-0 tw-relative": status === AdStatus.Ready && this.isFromGuide() && !isFirstReady,
    };
  }

  // Background is applied via an inline style rather than a Tailwind bg-color class: on this
  // <button>, tw-bg-color-* classes were not visibly taking effect (unlike on sibling <div>s),
  // so this bypasses whatever is winning the cascade to guarantee the color renders.
  private getContainerBg(
    status: AdStatus,
    { isFirstUnwatched, isLastCompleted }: GuideHighlightFlags,
  ): string {
    const isGuideHighlighted =
      (status === AdStatus.Completed &&
        this.isFromGuide() &&
        this.isWatchAdTaskSucceeded() &&
        isLastCompleted) ||
      (status === AdStatus.CoolDown &&
        this.isFromGuide() &&
        this.tryAgainLaterInTutorial() &&
        isFirstUnwatched) ||
      (status === AdStatus.Locked &&
        this.isFromGuide() &&
        this.tryAgainLaterInTutorial() &&
        isFirstUnwatched);

    if (isGuideHighlighted || status === AdStatus.Ready) {
      return "var(--color-surface-elevated)";
    }
    if (status === AdStatus.Locked || status === AdStatus.CoolDown) {
      return "var(--color-surface-sunken)";
    }
    if (status === AdStatus.Completed) {
      return "var(--color-status-success-bg)";
    }
    if (status === AdStatus.Interrupted || status === AdStatus.NoAdAvailable) {
      return "var(--color-status-warning-bg)";
    }
    return "";
  }

  private getTitleClass(status: AdStatus) {
    return {
      "tw-text-color-text-secondary": status === AdStatus.Locked,
      "tw-text-color-status-success-text": status === AdStatus.Completed,
      "tw-text-color-status-warning-text":
        status === AdStatus.Interrupted || status === AdStatus.NoAdAvailable,
      "tw-text-color-action-primary": status === AdStatus.Ready,
    };
  }

  private getPointsClass(status: AdStatus): string {
    if (status === AdStatus.Completed) {
      return "tw-text-color-status-success-text";
    }
    if (status === AdStatus.Ready) {
      return "tw-text-color-action-primary";
    }
    if (status === AdStatus.Interrupted || status === AdStatus.NoAdAvailable) {
      return "tw-text-color-text-disabled";
    }
    if (status === AdStatus.Locked || status === AdStatus.CoolDown) {
      return "tw-text-color-text-tertiary";
    }
    return "tw-text-color-text-default";
  }

  private getAdTitle(status: AdStatus): string {
    const titles: Record<AdStatus, string> = {
      [AdStatus.CoolDown]: "Locked",
      [AdStatus.Locked]: "Locked",
      [AdStatus.Interrupted]: "Interrupted",
      [AdStatus.Completed]: "Completed",
      [AdStatus.NoAdAvailable]: "No Ads Available",
      [AdStatus.Ready]: "Ready to Watch",
    };
    return titles[status];
  }

  private getAdDescription(status: AdStatus, timeRemaining?: number): string {
    switch (status) {
      case AdStatus.Completed:
        return "Thank you for watching";
      case AdStatus.Interrupted:
        return "Playback interrupted Session was interrupted";
      case AdStatus.Locked:
        if (timeRemaining && timeRemaining > 0) {
          return `Available in ${this.formatTimeRemaining(timeRemaining)}`;
        }
        return "Completed previous ad to unlock";
      case AdStatus.NoAdAvailable:
        return "Ads aren't available at the moment";
      default:
        return "Tap to start and earn points";
    }
  }

  // --- Component Actions ---

  showAdModal(adIndex: number): void {
    const ad = this.adStateService.adStates()[adIndex];
    const status = this.getAdStatus(ad);

    if (status === AdStatus.Completed || status === AdStatus.Locked || ad.isLoading) {
      return;
    }

    this.currentAdIndex = adIndex;

    if (this.isDesktop) {
      // Mark as interacting and clear transient failure flags so each open is a fresh
      // attempt; otherwise a previous no-fill (ad blocker, slow/empty response) leaves
      // the slot stuck as "NoAdAvailable" until the daily reset.
      this.updateGlobalState(adIndex, { isLoading: true, hasNoAds: false, isLoaded: false });
      this.showDesktopModal.set(true);
      return;
    }

    this.adInteractedWith(adIndex);
    this.clearWatchdog();

    // Only take the prepared fast path while the provider that prepared the ad is
    // still active, so its rewarded event is routed back to this component.
    if (this.isPrepared && this.adManager.hasPreparedAd()) {
      this.isPrepared = false;
      // The prepared ad already finished loading (that's what "prepared" means), and
      // showRewardVideoAd goes straight to playback -- no further Loaded event will
      // ever arrive to clear a load watchdog, so don't arm one here. A rewarded
      // fullscreen ad plus its end card routinely runs past any reasonable load
      // timeout, and doing so was firing mid-playback, nulling currentAdIndex before
      // the eventual Rewarded event and dropping an already-granted reward.
      this.adManager.showPreparedAd().catch(() => {
        // If the component was destroyed or the user backed out of this slot while
        // showRewardVideoAd() was rejecting, don't arm a watchdog or start a fallback
        // show whose listeners are already gone.
        if (this.destroyed || this.currentAdIndex !== adIndex) {
          return;
        }
        // The prepared ad couldn't be shown. Fall back to the full AdManager
        // flow so its provider-fallback (AdMob <-> LevelPlay) still applies,
        // mirroring the on-demand path. This path does still need a load
        // watchdog since a fresh load is about to be attempted.
        this.armLoadingWatchdog(adIndex);
        this.adManager.showAd(this.creditsService.userId() ?? undefined);
      });
    } else {
      this.armLoadingWatchdog(adIndex);
      this.adManager.showAd(this.creditsService.userId() ?? undefined);
    }
  }

  private armLoadingWatchdog(adIndex: number): void {
    this.loadingTimeout = setTimeout(() => {
      if (this.currentAdIndex === adIndex) {
        this.handleLoadingTimeout(adIndex);
      }
    }, 30000);
  }

  async onDesktopRewarded(): Promise<void> {
    const adIndex = this.currentAdIndex;
    this.currentAdIndex = null;
    // Unlike the mobile listener, a null adIndex here isn't a genuine server-granted
    // reward -- openTestAd() sets currentAdIndex to null deliberately before opening the
    // dev-only test modal, and desktop has no SSV to make that reward real. Skip
    // grantReward() in that case so the test button doesn't mint credits on dev/UAT.
    if (adIndex === null) {
      return;
    }

    // onRewarded is client-side UI state only (marks the ad watched, bumps the daily
    // count, starts the next-ad cooldown); it does not grant credits, so it always runs
    // immediately rather than waiting on the network calls below.
    this.onRewarded(adIndex);
    await this.grantReward();
  }

  // On PROD, Google Server-Side Verification creates the credit transaction via a
  // backend callback, so calling createTransactionOnTaskComplete from the client would
  // duplicate it -- only non-PROD needs the manual transaction.
  // TODO: the backend SSV callback must also be updated to issue 1 CrP (not 10 CrS) before this ships to PROD
  private async grantReward(): Promise<void> {
    if (this.creditsService.apiStage !== APIStageType.PROD) {
      // Said to be an ad reward: written as "manual" it read as Earned, not an ad watch, in
      // Recent Activity (gloss-client#3754).
      await this.creditsService.createTransactionOnTaskComplete(
        WATCH_AD_REWARD,
        GLOSS_PLATINUM_CREDITS_SYMBOL,
        "ad_reward",
      );
    }
    await this.glossCreditsTransactionSync.syncGlossCreditsTransactions();
  }

  onDesktopDismissed(): void {
    this.showDesktopModal.set(false);
    if (this.currentAdIndex !== null) {
      this.updateGlobalState(this.currentAdIndex, { isLoading: false });
      this.currentAdIndex = null;
    }
  }

  onDesktopFailedToLoad(): void {
    this.showDesktopModal.set(false);
    if (this.currentAdIndex !== null) {
      this.adFailedToLoad(this.currentAdIndex);
      this.currentAdIndex = null;
    }
  }

  openTestAd(): void {
    this.currentAdIndex = null;
    this.showDesktopModal.set(true);
  }

  private handleLoadingTimeout(adIndex: number): void {
    const ad = this.adStateService.adStates()[adIndex];

    if (ad && ad.isLoading) {
      this.globalService.showErrorMessage("error", "loadingTimeout");
      this.updateGlobalState(adIndex, { isLoading: false });
    }

    this.currentAdIndex = null;
    this.clearWatchdog();
  }

  private releaseAbandonedShow(): void {
    if (this.currentAdIndex !== null) {
      this.updateGlobalState(this.currentAdIndex, { isLoading: false });
      this.currentAdIndex = null;
      this.clearWatchdog();
    }
  }

  private clearWatchdog(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
  }

  private updateGlobalState(adIndex: number, partialState: Partial<AdState>): void {
    const currentAdStates = this.adStateService.adStates();
    const updatedAdStates = [...currentAdStates];
    updatedAdStates[adIndex] = { ...updatedAdStates[adIndex], ...partialState };

    this.adStateService.adStates.set(updatedAdStates);
    this.adStateService.saveStateToStorage();
  }

  adInteractedWith(adIndex: number): void {
    // Reset isLoaded so a stale flag from a previous dropped-reward attempt can't
    // render this slot as "Interrupted" (isLoaded && !watched) once this fresh
    // attempt starts.
    this.updateGlobalState(adIndex, { isLoading: true, isLoaded: false });
  }

  adFailedToLoad(adIndex: number): void {
    this.clearWatchdog();
    this.updateGlobalState(adIndex, { isLoading: false, isLoaded: false, hasNoAds: true });
  }

  onLoaded(adIndex: number): void {
    this.clearWatchdog();
    this.updateGlobalState(adIndex, { isLoading: false, isLoaded: true });
  }

  onRewarded(adIndex: number): void {
    this.clearWatchdog();
    this.updateGlobalState(adIndex, {
      watched: true,
      watchedAt: Date.now(),
      isRewareded: true,
      isLoaded: false,
      isLoading: false,
    });
    this.adStateService.adsWatchedToday++;
    if (this.isFromGuide()) {
      this.isWatchAdTaskSucceeded.set(true);
    }

    // Start timer for next ad only after current ad is successfully completed
    const currentStates = this.adStateService.adStates();
    if (adIndex < currentStates.length - 1) {
      this.adStateService.startTimerForAd(adIndex + 1);
    }
  }

  /**
   *
   * Data Bundles related actions - will be added back once able to create and offer bundled data
   *
   **/
  onViewBundleHistory(): void {
    this.viewBundleClick.emit();
  }

  onCreateBundle(): void {
    this.createBundleClick.emit();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.adListeners.forEach((l) => l?.remove());
    this.clearWatchdog();
    clearTimeout(this.preloadTimeout);
  }
}
